import { ArrowLeft, ArrowUpRight } from 'lucide-react'

import { mdxComponents } from '@/components/post/mdx-components'
import { SeriesNav } from '@/components/post/series-nav'
import { Badge } from '@/components/ui/badge'
import { formatDate } from '@/lib/date'
import type { Post } from '@/lib/posts'

type PostPageProps = {
  post: Post
}

export function PostPage({ post }: PostPageProps) {
  const Component = post.Component

  return (
    <article className="article">
      <a
        className="mb-8 inline-flex min-h-11 items-center gap-1 rounded-md text-sm font-medium text-muted-foreground no-underline hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        href="/posts"
      >
        <ArrowLeft className="h-4 w-4" /> Posts
      </a>
      <header className="mb-12 grid gap-6 border-b pb-8 sm:grid-cols-[8rem_minmax(0,1fr)]">
        <div className="space-y-3">
          <time className="type-metadata block" dateTime={post.date}>
            {formatDate(post.date)}
          </time>
          <div className="flex flex-wrap gap-2">
            {post.tags.map((tag) => (
              <a
                key={tag}
                className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                href={`/tags/${encodeURIComponent(tag)}`}
              >
                <Badge variant="outline">{tag}</Badge>
              </a>
            ))}
          </div>
        </div>
        <div className="space-y-4">
          <h1 className="type-article-title">{post.title}</h1>
          <p className="type-lede">{post.description}</p>
          {post.paper && (
            <a className="ink-link inline-flex min-h-11 items-center gap-1" href={post.paper}>
              Read the paper <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
            </a>
          )}
        </div>
      </header>
      <Component components={mdxComponents} />
      {post.aiDisclosure && (
        <section aria-labelledby="ai-use-disclosure" className="post-disclosure">
          <h2 id="ai-use-disclosure">AI use disclosure</h2>
          <p>{post.aiDisclosure}</p>
        </section>
      )}
      <SeriesNav post={post} />
    </article>
  )
}
